import React from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import Slider from "react-slick";
import { motion, AnimatePresence } from "framer-motion";

const settings = {
  dots: true,
  arrows: false,
  infinite: true,
  autoplay: true,
  speed: 1000,
  autoplaySpeed: 5000,
  slidesToShow: 1,
  slidesToScroll: 1,
  pauseOnHover: true,
};

interface BannerProps {
  images?: string[];
  banners?: any[];
  loading?: boolean;
  height?: string | { base: string; md: string };
}

export default function Banner({
  images,
  banners,
  loading,
  height,
}: BannerProps) {
  const [slider, setSlider] = React.useState<any>(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const cards =
    banners && banners.length > 0
      ? banners
      : images
        ? images.map((img) => ({ image: img }))
        : [];

  // Resolve height dynamically to maintain absolute premium fit across all screens
  let resolvedHeight = "520px";
  let mobileHeight = "280px";
  let tabletHeight = "390px";

  if (typeof height === "string") {
    const numHeight = parseInt(height);
    if (!isNaN(numHeight)) {
      resolvedHeight = `${numHeight}px`;
      mobileHeight = `${Math.round(numHeight * 0.5)}px`; // Mobile height scales down to 50%
      tabletHeight = `${Math.round(numHeight * 0.75)}px`; // Tablet height scales down to 75%
    } else {
      resolvedHeight = height;
      mobileHeight = height;
      tabletHeight = height;
    }
  } else if (height && typeof height === "object") {
    resolvedHeight = height.md || "520px";
    mobileHeight = height.base || "280px";
    tabletHeight = height.md
      ? `${Math.round(parseInt(height.md) * 0.75)}px`
      : "390px";
  }

  return (
    <div
      style={{
        position: "relative",
        height: "auto",
        width: "100%",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <style>
        {`
        .slick-dots { bottom: 20px !important; }
        .slick-dots li button:before { color: white !important; font-size: 10px !important; opacity: 0.5; }
        .slick-dots li.slick-active button:before { color: #01bdc0 !important; opacity: 1; }
        .slick-prev:before, .slick-next:before { content: none; }
        .banner-arrow {
            background: rgba(255,255,255,0.9) !important;
            backdrop-filter: blur(8px) !important;
            border: 2px solid #01bdc0 !important;
            color: #01bdc0 !important;
            cursor: pointer !important;
            position: absolute !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            z-index: 2 !important;
            transition: all 0.3s ease !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 46px !important;
            height: 46px !important;
            border-radius: 50% !important;
            box-shadow: 0 4px 12px rgba(1,189,192,0.3) !important;
            padding: 0 !important;
        }
        .banner-arrow:hover { 
            background: #01bdc0 !important; 
            color: #ffffff !important;
            transform: translateY(-50%) scale(1.08) !important; 
            box-shadow: 0 6px 18px rgba(1,189,192,0.5) !important;
        }
        .banner-arrow:hover svg {
            color: #ffffff !important;
        }
        .banner-arrow svg {
            font-size: 28px !important;
            color: #01bdc0 !important;
            transition: color 0.3s ease !important;
        }
        .banner-image {
            width: 100% !important;
            // object-fit: fit !important;
            height: ${resolvedHeight} !important;
            transition: height 0.3s ease !important;
            display: block !important;
        }
        
        @media (max-width: 1024px) {
            .banner-image { 
                height: ${tabletHeight} !important; 
            }
            .banner-arrow {
                width: 38px !important;
                height: 38px !important;
            }
            .banner-arrow svg {
                font-size: 22px !important;
            }
        }
        
        @media (max-width: 768px) {
            .banner-image { 
                height: ${mobileHeight} !important; 
            }
            .banner-arrow {
                width: 32px !important;
                height: 32px !important;
                left: 6px !important;
                right: 6px !important;
            }
            .banner-arrow svg {
                font-size: 18px !important;
            }
            .slick-dots { 
                bottom: 12px !important; 
            }
        }
        `}
      </style>

      <link
        rel="stylesheet"
        type="text/css"
        charSet="UTF-8"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />

      {/* Left Icon */}
      <button
        className="banner-arrow"
        style={{
          left: "12px",
          display: loading || cards.length === 0 ? "none" : "flex",
        }}
        onClick={() => slider?.slickPrev()}
        aria-label="Previous slide"
      >
        <BiChevronLeft />
      </button>

      {/* Right Icon */}
      <button
        className="banner-arrow"
        style={{
          right: "12px",
          display: loading || cards.length === 0 ? "none" : "flex",
        }}
        onClick={() => slider?.slickNext()}
        aria-label="Next slide"
      >
        <BiChevronRight />
      </button>

      {loading || cards.length === 0 ? (
        <div
          style={{
            width: "100%",
            height: resolvedHeight,
            backgroundColor: "#eee",
            position: "relative",
            overflow: "hidden",
            borderRadius: "16px",
          }}
        >
          <motion.div
            animate={{
              x: ["-100%", "100%"],
            }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: "linear",
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "40px",
              left: "8%",
              width: "40%",
            }}
          >
            <div
              style={{
                height: "40px",
                backgroundColor: "#ddd",
                borderRadius: "4px",
                marginBottom: "10px",
                width: "80%",
              }}
            />
            <div
              style={{
                height: "20px",
                backgroundColor: "#ddd",
                borderRadius: "4px",
                marginBottom: "20px",
                width: "100%",
              }}
            />
            <div
              style={{
                height: "45px",
                backgroundColor: "#ddd",
                borderRadius: "4px",
                width: "140px",
              }}
            />
          </div>
        </div>
      ) : (
        <Slider
          {...settings}
          ref={(s: any) => setSlider(s)}
          beforeChange={(_: number, next: number) => setCurrentSlide(next)}
        >
          {cards.map((el, index) => (
            // <div key={index} style={{ outline: "none", position: 'relative', }}>
            <img
              className="banner-image"
              src={el.image}
              alt={el.title || `Banner ${index}`}
              loading="lazy"
              style={{
                display: "block",
                pointerEvents: "auto",
                height: "100%",
                objectFit: "cover",
              }}
            />
            // </div>
          ))}
        </Slider>
      )}
    </div>
  );
}
